'use client';

import { ArrowRight, BotMessageSquare, ClipboardList, FileText, LoaderCircle, Settings, Users } from 'lucide-react';
import Link from 'next/link';
import { type ReactNode, useEffect, useState } from 'react';

import { PlannedModulesSidebar } from './planned-modules';

export type WorkspaceSummary = Readonly<{
  workspaceId: string;
  name: string;
  membershipId: string;
  role: 'OWNER' | 'EDITOR' | 'VIEWER';
  members: readonly Readonly<{ id: string; userId: string; email: string; role: 'OWNER' | 'EDITOR' | 'VIEWER'; status: 'ACTIVE' | 'DISABLED' }>[];
}>;

export function useWorkspaceSummary(): Readonly<{ workspace: WorkspaceSummary | null; loading: boolean; error: string | null }> {
  const [workspace, setWorkspace] = useState<WorkspaceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    void fetch('/api/workspace', { cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) throw new Error(await responseMessage(response));
        return response.json() as Promise<WorkspaceSummary>;
      })
      .then((value) => { if (active) setWorkspace(value); })
      .catch((caught: unknown) => { if (active) setError(caught instanceof Error ? caught.message : '工作区读取失败。'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);
  return { workspace, loading, error };
}

export function AppShell({ active, children }: Readonly<{ active: 'home' | 'trades' | 'documents' | 'customers' | 'settings'; children: ReactNode }>) {
  const { workspace, loading, error } = useWorkspaceSummary();
  return (
    <main className="app-shell">
      <aside className="side-nav" aria-label="TradeTool navigation">
        <Link className="brand-block" href="/">
          <span className="brand-mark">T</span>
          <span className="brand-name">TradeTool</span>
        </Link>
        <nav className="nav-list" aria-label="Workspace sections">
          <NavLink active={active === 'home'} href="/" icon={<BotMessageSquare size={18} aria-hidden="true" />}>制单工作台</NavLink>
          <NavLink active={active === 'trades'} href="/trades" icon={<ClipboardList size={18} aria-hidden="true" />}>历史记录</NavLink>
          <NavLink active={active === 'documents'} href="/documents" icon={<FileText size={18} aria-hidden="true" />}>单据</NavLink>
          <NavLink active={active === 'customers'} href="/customers" icon={<Users size={18} aria-hidden="true" />}>客户</NavLink>
          <NavLink active={active === 'settings'} href="/settings" icon={<Settings size={18} aria-hidden="true" />}>设置</NavLink>
        </nav>
        <PlannedModulesSidebar />
        <div className="nav-footer">
          <span className="status-dot" aria-hidden="true" />
          {loading ? '正在读取工作区' : error ? '会话需要重新登录' : workspace?.name ?? '未绑定工作区'}
        </div>
      </aside>
      <section className="workspace">
        {active === 'home'
          ? (!workspace ? <header className="workspace-context workspace-context-home"><Link className="inline-action" href="/login">前往登录 <ArrowRight size={15} /></Link></header> : null)
          : <header className="workspace-context">
            <div><p className="eyebrow">当前工作区</p><strong>{workspace?.name ?? (loading ? '读取中…' : '需要登录')}</strong></div>
            {workspace ? <span className="role-chip">{roleLabel(workspace.role)}</span> : <Link className="inline-action" href="/login">前往登录 <ArrowRight size={15} /></Link>}
          </header>}
        {children}
      </section>
    </main>
  );
}

function NavLink({ active, href, icon, children }: Readonly<{ active: boolean; href: string; icon: ReactNode; children: ReactNode }>) {
  return <Link className={active ? 'nav-item nav-item-active' : 'nav-item'} href={href}>{icon}{children}</Link>;
}

function roleLabel(role: WorkspaceSummary['role']): string {
  return role === 'OWNER' ? 'Owner' : role === 'EDITOR' ? 'Editor' : 'Viewer';
}

export function LoadingPanel({ label = '正在读取…' }: Readonly<{ label?: string }>) {
  return <div className="page-loading" role="status"><LoaderCircle className="spin" size={20} />{label}</div>;
}

export async function responseMessage(response: Response): Promise<string> {
  try {
    const payload = await response.json() as { message?: string; code?: string; fields?: Record<string, string> };
    if (payload.code === 'SUBSCRIPTION_READ_ONLY') return '当前订阅已暂停、取消或到期，工作区为只读状态。请在设置中检查订阅状态。';
    if (payload.message) return payload.message;
    return payload.code ? `${payload.code}（${response.status}）` : `请求失败（${response.status}）`;
  } catch { return `请求失败（${response.status}）`; }
}

export function mutationHeaders(): HeadersInit {
  return { 'content-type': 'application/json', 'idempotency-key': crypto.randomUUID() };
}
